// texe-web/src/context/RealtimeProvider.tsx
import { useEffect, useRef } from "react";
import type { PropsWithChildren } from "react";
import { openRealtimeStream, refreshAccess, getToken } from "../lib/api";
import { realtimeBus } from "../realtime/RealtimeBus";
import { useAuth } from "./AuthProvider";

// refresh'i 3sn ile sınırla
async function refreshQuick(ms = 3000) {
  await Promise.race([
    refreshAccess().catch(() => undefined),
    new Promise<void>((resolve) => setTimeout(() => resolve(), ms)),
  ]);
}

/**
 * Uygulama genelinde TEK bir SSE bağlantısı açar.
 * Gelen event’leri RealtimeBus’a yayınlar.
 * (Hata durumunda uygulamayı çökertmez.)
 */
export default function RealtimeProvider({ children }: PropsWithChildren) {
  const { user } = useAuth();
  const sseRef = useRef<ReturnType<typeof openRealtimeStream> | null>(null);

  // Watchdog için; en son “yaşadığımızı gösteren” zaman damgası
  const lastLivenessAtRef = useRef<number>(Date.now());

  useEffect(() => {
    // Kullanıcı giriş yapmamışsa SSE bağlantısı açma
    if (!user || !getToken()) {
      // Mevcut bağlantıyı kapat
      try {
        sseRef.current?.close();
      } catch {}
      sseRef.current = null;
      return;
    }

    let cancelled = false;

    // Tekrarlanabilir bağlanma fonksiyonu
    const connect = async () => {
      try {
        await refreshQuick(3000);
        if (cancelled) return;

        // Eski bağlantı varsa kapat
        try {
          sseRef.current?.close();
        } catch {}
        sseRef.current = null;

        lastLivenessAtRef.current = Date.now();

        sseRef.current = openRealtimeStream(
          {
            onOpen: () => {
              lastLivenessAtRef.current = Date.now();
            },
            onClose: () => {
              // connection closed
            },
            onError: () => {
              // connection error
            },

            // Sunucudan 25 sn’de gelen ping’ler
            onPing: (t) => {
              lastLivenessAtRef.current = Date.now();
              realtimeBus.emitPing(t);
            },

            // Standart kısa yollar
            onNotification: (payload) => {
              lastLivenessAtRef.current = Date.now();
              realtimeBus.emitNotification({ payload });
            },
            onFeedNewPost: (payload) => {
              lastLivenessAtRef.current = Date.now();
              realtimeBus.emitFeedNewPost(payload);
            },
            onFeedDeletePost: (payload) => {
              lastLivenessAtRef.current = Date.now();
              realtimeBus.emitFeedDeletePost(payload);
            },

            // Ham mesajlar (comment:* buradan akar)
            onRaw: ({ event, data }) => {
              try {
                if (event !== "message" || !data || typeof data !== "object") return;

                lastLivenessAtRef.current = Date.now();

                const type = (data as any).type as string | undefined;
                if (!type) return;

                if (type === "comment:new" && (data as any).postId && (data as any).comment) {
                  realtimeBus.emitCommentNew({
                    postId: String((data as any).postId),
                    comment: (data as any).comment,
                  });
                } else if (type === "comment:edit" && (data as any).postId && (data as any).comment) {
                  realtimeBus.emitCommentEdit({
                    postId: String((data as any).postId),
                    comment: (data as any).comment,
                  });
                } else if (
                  type === "comment:delete" &&
                  (data as any).postId &&
                  ((data as any).commentId || (data as any).id)
                ) {
                  realtimeBus.emitCommentDelete({
                    postId: String((data as any).postId),
                    commentId: String((data as any).commentId ?? (data as any).id),
                  });
                }
              } catch {
                // parse error
              }
            },
          },
          { maxBackoffMs: 15000 } // mevcut geri dönüşümlü bağlanma stratejine saygı
        );
      } catch {
        // connection failed
      }
    };

    // İlk bağlantı
    connect();

    // ---- Watchdog: 30 sn’de bir canlılık kontrolü ----
    const watchdog = setInterval(() => {
      const since = Date.now() - lastLivenessAtRef.current;
      // 60 sn boyunca ping/mesaj yoksa bağlantıyı kibarca yenile
      if (since > 60_000) {
        connect();
      }
    }, 30_000);

    // ---- Görünürlük: sekme geri gelince kontrol et ----
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // görünür olduğumuzda “ölü” bağlantı varsa yenile
        const since = Date.now() - lastLivenessAtRef.current;
        if (since > 45_000) {
          connect();
        }
      }
    };

    // ---- Ağ tekrar geldiyse yeniden bağlan ----
    const onOnline = () => connect();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);

    return () => {
      cancelled = true;
      clearInterval(watchdog);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      try {
        sseRef.current?.close();
      } catch {}
      sseRef.current = null;
    };
  }, [user]); // user değişince yeniden çalışsın

  return <>{children}</>;
}
